import { useState, useEffect } from "react";
import { CheckCircle, AlertTriangle, XCircle, X } from "lucide-react";
import { Button } from "../../ui/button";

interface ActionStatusProps {
  show: boolean;
  message: string;
  type: "success" | "error" | "warning";
  onClose: () => void;
  autoClose?: number; // Auto close after X milliseconds
}

export default function ActionStatus({
  show,
  message,
  type,
  onClose,
  autoClose = 5000
}: ActionStatusProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (show) {
      setIsVisible(true);
      
      if (autoClose) {
        const timer = setTimeout(() => {
          handleClose();
        }, autoClose);
        
        return () => clearTimeout(timer);
      }
    }
  }, [show, autoClose]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300); // Allow animation to complete
  };

  const getStatusStyles = () => {
    switch (type) {
      case "success":
        return {
          bg: "bg-green-600",
          icon: <CheckCircle className="w-5 h-5 text-white" />,
          text: "text-white"
        };
      case "error":
        return {
          bg: "bg-red-600",
          icon: <XCircle className="w-5 h-5 text-white" />,
          text: "text-white"
        };
      case "warning":
        return {
          bg: "bg-amber-600",
          icon: <AlertTriangle className="w-5 h-5 text-white" />,
          text: "text-white"
        };
      default:
        return {
          bg: "bg-blue-600",
          icon: <CheckCircle className="w-5 h-5 text-white" />,
          text: "text-white"
        };
    }
  };

  if (!show) return null;

  const { bg, icon, text } = getStatusStyles();

  return (
    <div className="fixed z-50 top-4 right-4">
      <div 
        className={`
          ${bg} ${text} rounded-lg shadow-lg px-4 py-3 min-w-[280px] max-w-[400px]
          flex items-center justify-between gap-3
          transform transition-all duration-300 ease-in-out
          ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
        `}
        data-testid="action-status"
      >
        <div className="flex items-center gap-3">
          <div data-testid="status-icon" className="flex-shrink-0">
            {icon}
          </div>
          <span className="text-sm font-medium">
            {message}
          </span>
        </div>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClose}
          className="w-6 h-6 text-white hover:bg-white/20"
          data-testid="button-close-status"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}